/** @jsx isolateComponent */

import { camelCase } from 'lodash';
import { Fragment } from 'react';

import {
  getLowestPlan,
  getTrialUrl,
} from '@zapier/design-system-revenue/src/utils';
import { isolateComponent } from '@zapier/common-utils';
import { useLink } from '@zapier/common-hooks';
import { Button, Heading, Spacer, Text } from '@zapier/design-system';
import { ContentLoader, FlexLayout } from '@zapier/common-components';
import {
  SIGNUP_PRE_INTENT_EVENT,
  useMaybeSendFullStoryEvent,
  useTrackEvent,
  useTrackingContext,
} from '@zapier/toolbox-tracking';

import {
  FeatureData,
  FeatureList,
} from '@zapier/design-system-revenue/src/components/offers';
import { Price } from '@zapier/design-system-revenue/src/components/other/Price';
import { useCurrencies } from '@zapier/design-system-revenue/src/hooks';
import { Size, TrialType } from '@zapier/design-system-revenue/src/types';

import { useDefaultPlans } from '../useDefaultPlans';

export type SharedTrialOfferProps = {
  /**
   * The text to display for the decline offer option.
   * @default 'Back'
   **/
  declineText?: string;
  /** The function called when the offer is declined. */
  onDecline: () => void;
  /** The size of the viewport. */
  size?: Size;
};

type TrialOfferProps = SharedTrialOfferProps & {
  /** The text to display under the title. */
  description: string;
  /** An array of plan features to display. */
  features: Array<FeatureData>;
  /** The name of the custom FullStory event to send when the component renders. */
  fullStoryEventName?: string;
  /** The modal title. */
  title: string;
  /** The type of trial. */
  trialType: TrialType;
};

/**
 * A modal that offers the opportunity to start a trial.
 */
export const TrialOffer = ({
  declineText = 'Back',
  description,
  features,
  fullStoryEventName,
  onDecline,
  size,
  title,
  trialType,
}: TrialOfferProps) => {
  const { routeTo } = useLink();
  const { trackEvent } = useTrackingContext();
  const { isLoading: isLoadingCurrencies, selectedCurrency } = useCurrencies();
  const { planStacks } = useDefaultPlans();
  const lowestPrice = getLowestPlan(planStacks, trialType)?.amount;
  const trialUrl = getTrialUrl(trialType);
  const acceptText = 'Start 14-day trial';

  useMaybeSendFullStoryEvent(
    !!fullStoryEventName,
    fullStoryEventName as string
  );
  useTrackEvent(`${trialType}-trial-offer-shown`);

  return (
    <Fragment>
      <Heading>
        <Text type="header800">{title}</Text>
      </Heading>

      <Spacer height={10} />

      <Text tag="p" type="bodyText900">
        {description}
      </Text>

      <Spacer height={40} />

      <FeatureList
        features={features}
        fontWeight={600}
        iconColor="green400"
        iconSize={27}
        textType="bodyText900"
      />

      <Spacer height={40} />

      {isLoadingCurrencies || lowestPrice === undefined ? (
        <ContentLoader height="25px" />
      ) : (
        <Text color="grey700" tag="p" type="bodyText900">
          Plans from{' '}
          <Price currency={selectedCurrency}>{lowestPrice / 12}</Price>
          /mo
        </Text>
      )}

      <FlexLayout
        direction={size === 'small' ? 'column' : 'row'}
        gutter={size === 'small' ? 'none' : 'normal'}
        justify="flex-end"
      >
        <Button
          color="greyAlt"
          isFullWidth={size === 'small'}
          onClick={() => {
            trackEvent(`${trialType}-trial-offer-declined`);
            onDecline();
          }}
        >
          {declineText}
        </Button>

        <FlexLayout direction="column" gutter="extraSmall">
          <Button
            hasShadow={true}
            isFullWidth={size === 'small'}
            onClick={() => {
              trackEvent(SIGNUP_PRE_INTENT_EVENT, {
                ctaName: camelCase(acceptText),
                cta_raw_text: acceptText,
                linkDestination: trialUrl,
                signupFormat: 'button',
              });

              if (routeTo) {
                routeTo(trialUrl);
              }
            }}
          >
            {acceptText}
          </Button>

          <Text color="grey700" tag="p" textAlign="center" type="altText900">
            No credit card needed
          </Text>
        </FlexLayout>
      </FlexLayout>
    </Fragment>
  );
};
